import React, { useEffect, useState } from "react";
import Loading from "../components/Loading";
import { clearLocalIdentity, validateCentralSession } from "./identitySession";
import { SESSION_STATE } from "./sessionLifecycle";
import { getAuthenticatedStoreDestination, saveIdentityDestination } from "./identityClient";

/**
 * AuthEntryGate prevents already-authenticated Store users from being forced
 * through the cinematic arrival + Accounts ceremony again.
 *
 * A pricing/commerce request such as /signup?planId=... is converted into the
 * canonical /setup-flow?... destination before session validation. If the
 * central Accounts session is active we go straight there; otherwise the
 * normal login/signup experience is rendered unchanged.
 */
export default function AuthEntryGate({ children }) {
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Capture commerce intent before deciding whether authentication UI is
      // necessary. This also keeps login <-> signup switching lossless.
      saveIdentityDestination();

      let state = await validateCentralSession();

      // A transient Accounts/network miss must not instantly turn an existing
      // Store session into a logout. Retry once, then expose the normal gateway.
      if (state === SESSION_STATE.UNVERIFIABLE) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        state = await validateCentralSession();
      }

      if (cancelled) return;

      if (state === SESSION_STATE.ACTIVE) {
        window.location.replace(getAuthenticatedStoreDestination());
        return;
      }

      if (state === SESSION_STATE.INACTIVE) clearLocalIdentity();
      setChecking(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return <Loading message="Checking your Clikkle session…" />;
  }

  return children;
}
